// Trading chart built on TradingView Lightweight Charts (Apache-2.0, attribution shown on the chart).
// Features: all timeframes, scroll-back history, zoom, indicators, drawing tools, position / order lines,
// drag-to-move stop-loss and take-profit, one-click trading. Falls back to the classic chart if the library is missing.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../vendor/lightweight-charts.js';
import ChartClassic from './ChartClassic.jsx';
import { api } from './api.js';
import { meta, num } from './format.js';
import { bollinger, ema, macd, rsi, sma, vwap } from './indicators.js';
import { t, tk } from './i18n.js';
import { useTheme } from './theme.js';

const MAIN_TF = ['1m', '5m', '15m', '1h', '4h', '1d', '1w', '1M'];
const MORE_TF = ['3m', '30m', '2h', '12h'];
const TF_MS = { '1m': 60e3, '3m': 180e3, '5m': 300e3, '15m': 900e3, '30m': 1800e3, '1h': 3600e3, '2h': 7200e3, '4h': 14400e3, '12h': 43200e3, '1d': 86400e3, '1w': 604800e3, '1M': 2592000e3 };
const TF_LABEL = { '1m': '1m', '3m': '3m', '5m': '5m', '15m': '15m', '30m': '30m', '1h': '1H', '2h': '2H', '4h': '4H', '12h': '12H', '1d': '1D', '1w': '1W', '1M': '1M' };
// Indicator line colours are the same in both themes; everything else comes from the DARK / LIGHT palette.
const IC = { violet: '#C084FC', cyan: '#38BDF8', amber: '#F59E0B', pink: '#F472B6', slate: '#64748B', vwap: '#22D3EE' };
const DARK = { ...IC, up: '#1FC68A', down: '#F2495C', grid: '#131821', text: '#8D96A8', bg: '#0A0D12', line: '#1C222D', warn: '#E8B04A', accent: '#8B9CFF',
  crosshair: '#55607A', labelBg: '#2A3240', sepHover: '#2A3240', rsiLevel: '#3A4252', trend: '#C9D1FF', hline: '#9AA4B8', orderBuy: '#3BA67F', orderSell: '#C2505E',
  volUp: 'rgba(31,198,138,0.28)', volDown: 'rgba(242,73,92,0.28)', macdUp: 'rgba(31,198,138,0.55)', macdDown: 'rgba(242,73,92,0.55)' };
const LIGHT = { ...IC, up: '#0E9F6E', down: '#E0364B', grid: '#EEF1F5', text: '#5A6476', bg: '#FFFFFF', line: '#E2E7EE', warn: '#B7791F', accent: '#4F5FE0',
  crosshair: '#9AA3B2', labelBg: '#E9EDF3', sepHover: '#E9EDF3', rsiLevel: '#C3CBD8', trend: '#4F5FE0', hline: '#6B7280', orderBuy: '#2F8F6B', orderSell: '#C2405A',
  volUp: 'rgba(14,159,110,0.28)', volDown: 'rgba(224,54,75,0.28)', macdUp: 'rgba(14,159,110,0.55)', macdDown: 'rgba(224,54,75,0.55)' };
const chartColors = (P) => ({
  layout: { background: { type: 'solid', color: P.bg }, textColor: P.text, panes: { separatorColor: P.line, separatorHoverColor: P.sepHover } },
  grid: { vertLines: { color: P.grid }, horzLines: { color: P.grid } },
  crosshair: { vertLine: { color: P.crosshair, labelBackgroundColor: P.labelBg }, horzLine: { color: P.crosshair, labelBackgroundColor: P.labelBg } },
  rightPriceScale: { borderColor: P.line },
  timeScale: { borderColor: P.line },
});

export const INDICATORS = [
  { id: 'vol', label: tk('Volume') },
  { id: 'ma20', label: tk('MA 20'), color: IC.amber },
  { id: 'ma50', label: tk('MA 50'), color: IC.cyan },
  { id: 'ema20', label: tk('EMA 20'), color: IC.pink },
  { id: 'ema200', label: tk('EMA 200'), color: IC.violet },
  { id: 'bb', label: tk('Bollinger Bands (20, 2)'), color: IC.slate },
  { id: 'vwap', label: tk('VWAP'), color: IC.vwap },
  { id: 'rsi', label: tk('RSI (14)'), color: IC.violet, pane: true },
  { id: 'macd', label: tk('MACD (12, 26, 9)'), color: IC.cyan, pane: true },
];

const store = {
  get(k, d) { try { const v = localStorage.getItem(k); return v == null ? d : JSON.parse(v); } catch { return d; } },
  set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } },
};

export default function Chart(props) {
  const lib = typeof window !== 'undefined' && window.LightweightCharts;
  const [classic, setClassic] = useState(() => !lib || store.get('chartClassic', false));
  const toggle = (v) => { setClassic(v); store.set('chartClassic', v); };
  if (classic || !lib) {
    return <ChartClassic {...props} toolbarExtra={lib ? <button className="tf" onClick={() => toggle(false)} title={t('Switch to the TradingView chart')}>{t('TradingView chart')}</button> : null} />;
  }
  return <TvChart {...props} onClassic={() => toggle(true)} />;
}

// Chart time: seconds, shifted to local time for intraday bars so the axis shows the trader's clock.
// Some systems report locales like "en-US@posix" that Intl rejects; fall back to en-US.
const safeLocale = () => {
  const l = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
  try { new Intl.DateTimeFormat(l); return l; } catch { return 'en-US'; }
};
const tzShift = () => -new Date().getTimezoneOffset() * 60;
const toTime = (t, tf) => Math.floor(t / 1000) + (TF_MS[tf] < 86400e3 ? tzShift() : 0);
const nextBarTime = (t, tf) => {
  if (tf !== '1M') return t + TF_MS[tf];
  const d = new Date(t); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
};

function TvChart({ symbol, lastPrice, position, breachPrice, breachLabel, orders = [], onMoveProtection, oneClick, onQuickOrder, onClassic }) {
  const L = window.LightweightCharts;
  const m = meta(symbol);
  const dp = m.priceDp;
  const [tf, setTf] = useState(() => store.get('chartTf', '15m'));
  const [ind, setInd] = useState(() => store.get('chartIndicators', ['vol', 'ma20']));
  const [tool, setTool] = useState('none');
  const [drawings, setDrawings] = useState(() => store.get(`drawings:${symbol}`, []));
  const [menu, setMenuRaw] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  // Menus open as floating panels placed under their button, so the toolbar never clips them.
  const openMenu = (name, e, width = 280) => {
    if (menu === name) { setMenuRaw(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const vw = window.innerWidth;
    const rtl = document.documentElement.dir === 'rtl';
    let left = rtl ? r.right - width : r.left;
    left = Math.max(8, Math.min(left, vw - width - 8));
    setMenuPos({ top: r.bottom + 6, left, maxHeight: Math.max(160, window.innerHeight - r.bottom - 16) });
    setMenuRaw(name);
  };
  const setMenu = setMenuRaw;
  const [legend, setLegend] = useState(null);
  const [status, setStatus] = useState('loading');
  const [full, setFull] = useState(false);
  const [qcQty, setQcQty] = useState(() => store.get(`qcQty:${symbol}`, ''));
  const [qcBusy, setQcBusy] = useState(false);
  const sectionRef = useRef(null);
  const boxRef = useRef(null);
  const R = useRef({}); // chart, series, candles, lines, etc. (imperative state)
  const theme = useTheme();
  const P = theme === 'light' ? LIGHT : DARK;
  R.current.P = P;

  useEffect(() => store.set('chartTf', tf), [tf]);
  useEffect(() => {
    const f = (e) => { const t = MAIN_TF[e.detail]; if (t) setTf(t); };
    window.addEventListener('fx:timeframe', f);
    return () => window.removeEventListener('fx:timeframe', f);
  }, []);
  useEffect(() => store.set('chartIndicators', ind), [ind]);
  useEffect(() => { setDrawings(store.get(`drawings:${symbol}`, [])); setQcQty(store.get(`qcQty:${symbol}`, '')); setTool('none'); }, [symbol]);
  useEffect(() => store.set(`drawings:${symbol}`, drawings), [drawings, symbol]);
  useEffect(() => store.set(`qcQty:${symbol}`, qcQty), [qcQty, symbol]);
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onScroll = (e) => { if (!(e.target instanceof Element && e.target.closest('.menu'))) setMenu(null); };
    window.addEventListener('click', close);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', onScroll, true);
    return () => { window.removeEventListener('click', close); window.removeEventListener('resize', close); window.removeEventListener('scroll', onScroll, true); };
  }, [menu]);
  useEffect(() => {
    const f = () => setFull(document.fullscreenElement === sectionRef.current);
    document.addEventListener('fullscreenchange', f);
    return () => document.removeEventListener('fullscreenchange', f);
  }, []);

  // ---------------------------------------------------------------- create the chart once
  useEffect(() => {
    const P0 = R.current.P;
    const chart = L.createChart(boxRef.current, {
      autoSize: true,
      layout: { background: { type: 'solid', color: P0.bg }, textColor: P0.text, fontFamily: "'Geist', system-ui, sans-serif", fontSize: 11, attributionLogo: true,
        panes: { separatorColor: P0.line, separatorHoverColor: P0.sepHover, enableResize: true } },
      grid: { vertLines: { color: P0.grid }, horzLines: { color: P0.grid } },
      crosshair: { mode: L.CrosshairMode.Normal, vertLine: { color: P0.crosshair, labelBackgroundColor: P0.labelBg }, horzLine: { color: P0.crosshair, labelBackgroundColor: P0.labelBg } },
      rightPriceScale: { borderColor: P0.line, scaleMargins: { top: 0.08, bottom: 0.08 } },
      timeScale: { borderColor: P0.line, timeVisible: true, secondsVisible: false, rightOffset: 6, barSpacing: 8, minBarSpacing: 0.5 },
      kineticScroll: { touch: true, mouse: false },
      localization: { locale: safeLocale() },
    });
    const candle = chart.addSeries(L.CandlestickSeries, {
      upColor: P0.up, downColor: P0.down, wickUpColor: P0.up, wickDownColor: P0.down, borderVisible: false,
    });
    Object.assign(R.current, { chart, candle, candles: [], ind: [], lines: [], trend: [], loadingMore: false, reachedStart: false, pending: null, dragging: null });
    chart.subscribeCrosshairMove((p) => {
      const d = p && p.time != null ? p.seriesData.get(candle) : null;
      setLegend(d ? { o: d.open, h: d.high, l: d.low, c: d.close } : null);
    });
    chart.subscribeClick((p) => R.current.onClick && R.current.onClick(p));
    if (chart.subscribeDblClick) chart.subscribeDblClick((p) => R.current.onClick && R.current.onClick(p)); // quick second click
    try { if (localStorage.getItem('debugChart')) window.__fxChart = R.current; } catch { /* ignore */ }
    chart.timeScale().subscribeVisibleLogicalRangeChange((r) => { if (r && r.from < 30) R.current.loadMore && R.current.loadMore(); });
    return () => { chart.remove(); Object.assign(R.current, { chart: null, candle: null, lines: [], trend: [], ind: [] }); };
  }, [L]);

  // Follow the light / dark theme: recolour the chart, candles, indicators and lines.
  const themeRef = useRef(theme);
  useEffect(() => {
    if (themeRef.current === theme) return;
    themeRef.current = theme;
    const r = R.current; if (!r.chart) return;
    r.chart.applyOptions(chartColors(P));
    r.candle.applyOptions({ upColor: P.up, downColor: P.down, wickUpColor: P.up, wickDownColor: P.down });
    r.drawIndicators && r.drawIndicators();
    if (!r.dragging) r.redrawLines && r.redrawLines();
    r.redrawTrend && r.redrawTrend();
  }, [theme, P]);

  // ---------------------------------------------------------------- data
  const bars = useCallback((candles) => candles.map((c) => ({ time: toTime(c.t, tf), open: c.o, high: c.h, low: c.l, close: c.c })), [tf]);
  const vols = useCallback((candles) => candles.map((c) => ({ time: toTime(c.t, tf), value: c.v || 0, color: c.c >= c.o ? P.volUp : P.volDown })), [tf, P]);

  const drawIndicators = useCallback(() => {
    const r = R.current; if (!r.chart) return;
    for (const s of r.ind) { try { r.chart.removeSeries(s); } catch { /* gone */ } }
    r.ind = [];
    const panes = r.chart.panes();
    for (let i = panes.length - 1; i > 0; i--) { try { r.chart.removePane(i); } catch { /* ignore */ } }
    const cs = r.candles; if (!cs.length) return;
    const T = (i) => toTime(cs[i].t, tf);
    const line = (pts, color, opts = {}, pane = 0) => {
      const s = r.chart.addSeries(L.LineSeries, { color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false, ...opts }, pane);
      s.setData(pts.map((p) => ({ time: T(p.i), value: p.value })));
      r.ind.push(s); return s;
    };
    const has = (id) => ind.includes(id);
    if (has('vol')) {
      const v = r.chart.addSeries(L.HistogramSeries, { priceFormat: { type: 'volume' }, priceScaleId: 'vol', lastValueVisible: false, priceLineVisible: false });
      v.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
      v.setData(vols(cs)); r.ind.push(v); r.vol = v;
    } else r.vol = null;
    if (has('ma20')) line(sma(cs, 20), P.amber);
    if (has('ma50')) line(sma(cs, 50), P.cyan);
    if (has('ema20')) line(ema(cs, 20), P.pink);
    if (has('ema200')) line(ema(cs, 200), P.violet);
    if (has('bb')) { const b = bollinger(cs); line(b.upper, P.slate); line(b.mid, P.slate, { lineStyle: 2 }); line(b.lower, P.slate); }
    if (has('vwap') && TF_MS[tf] < 86400e3) line(vwap(cs), P.vwap, { lineWidth: 2 });
    let pane = 1;
    if (has('rsi')) {
      const s = line(rsi(cs), P.violet, { lastValueVisible: true, priceFormat: { type: 'price', precision: 1, minMove: 0.1 } }, pane);
      s.createPriceLine({ price: 70, color: P.rsiLevel, lineStyle: 2, lineWidth: 1, axisLabelVisible: false });
      s.createPriceLine({ price: 30, color: P.rsiLevel, lineStyle: 2, lineWidth: 1, axisLabelVisible: false });
      pane++;
    }
    if (has('macd')) {
      const mc = macd(cs);
      const h = r.chart.addSeries(L.HistogramSeries, { priceLineVisible: false, lastValueVisible: false, priceFormat: { type: 'price', precision: dp, minMove: 1 / 10 ** dp } }, pane);
      h.setData(mc.hist.map((p) => ({ time: T(p.i), value: p.value, color: p.value >= 0 ? P.macdUp : P.macdDown })));
      r.ind.push(h);
      line(mc.line, P.cyan, { priceFormat: { type: 'price', precision: dp, minMove: 1 / 10 ** dp } }, pane);
      line(mc.signal, P.amber, { priceFormat: { type: 'price', precision: dp, minMove: 1 / 10 ** dp } }, pane);
      pane++;
    }
    // Main chart gets most of the height; RSI / MACD panes share the rest.
    const ps = r.chart.panes();
    try { ps.forEach((pn, i) => pn.setStretchFactor(i === 0 ? 4 : 1)); } catch { /* older library */ }
  }, [L, ind, tf, vols, dp, P]);

  // Load history when the market or timeframe changes.
  useEffect(() => {
    const r = R.current; if (!r.chart) return undefined;
    let alive = true;
    setStatus('loading');
    r.candles = []; r.reachedStart = false; r.loadingMore = false;
    r.candle.applyOptions({ priceFormat: { type: 'price', precision: dp, minMove: 1 / 10 ** dp } });
    r.candle.setData([]);
    api('GET', `/api/candles?symbol=${symbol}&interval=${tf}&limit=500`).then((cs) => {
      if (!alive || !R.current.chart) return;
      r.candles = cs;
      r.candle.setData(r.bars(cs));
      r.drawIndicators();
      const n = cs.length;
      r.chart.timeScale().setVisibleLogicalRange({ from: Math.max(0, n - 120), to: n + 6 });
      setStatus(n ? 'ok' : 'empty');
      r.redrawLines && r.redrawLines();
      r.redrawTrend && r.redrawTrend();
    }).catch(() => alive && setStatus('error'));
    return () => { alive = false; };
  }, [symbol, tf, dp]); // eslint-disable-line react-hooks/exhaustive-deps

  R.current.drawIndicators = drawIndicators;
  R.current.bars = bars;
  useEffect(() => { drawIndicators(); }, [drawIndicators]);

  // Scrolling back: load older candles and keep the view where it was.
  R.current.loadMore = async () => {
    const r = R.current;
    if (!r.chart || r.loadingMore || r.reachedStart || !r.candles.length) return;
    r.loadingMore = true;
    const first = r.candles[0].t;
    const sym = symbol, interval = tf;
    try {
      const older = await api('GET', `/api/candles?symbol=${sym}&interval=${interval}&limit=500&before=${first}`);
      if (!R.current.chart || sym !== symbol || interval !== tf || R.current.candles[0]?.t !== first) return;
      const add = older.filter((c) => c.t < first);
      if (!add.length) { r.reachedStart = true; return; }
      const range = r.chart.timeScale().getVisibleLogicalRange();
      r.candles = [...add, ...r.candles];
      r.candle.setData(r.bars(r.candles));
      r.drawIndicators();
      if (range) r.chart.timeScale().setVisibleLogicalRange({ from: range.from + add.length, to: range.to + add.length });
      r.redrawTrend && r.redrawTrend();
    } catch { /* try again on next scroll */ } finally { r.loadingMore = false; }
  };

  // Live price: update the forming candle, open a new one when the bar time is up.
  const lastInd = useRef(0);
  useEffect(() => {
    const r = R.current;
    if (!r.chart || lastPrice == null || !r.candles.length) return;
    const cs = r.candles;
    let last = cs[cs.length - 1];
    let fresh = false;
    while (Date.now() >= nextBarTime(last.t, tf)) {
      const t = nextBarTime(last.t, tf);
      last = { t, o: last.c, h: last.c, l: last.c, c: last.c, v: 0 };
      cs.push(last); fresh = true;
      if (Date.now() < nextBarTime(t, tf)) break;
    }
    last.c = lastPrice; last.h = Math.max(last.h, lastPrice); last.l = Math.min(last.l, lastPrice);
    r.candle.update({ time: toTime(last.t, tf), open: last.o, high: last.h, low: last.l, close: last.c });
    if (fresh || Date.now() - lastInd.current > 2000) {
      lastInd.current = Date.now();
      if (fresh) r.drawIndicators();
    }
  }, [lastPrice, tf]);

  // ---------------------------------------------------------------- price lines (position, SL/TP, orders, breach, user lines)
  const lineSpec = useMemo(() => {
    const out = [];
    if (position) {
      const pnl = position.netPnl != null ? position.netPnl : position.unrealizedPnl;
      out.push({ key: 'entry', price: position.entryPrice, color: P.accent, style: 0, width: 1,
        title: t('{side} {qty}  {pnl} net', { side: position.side === 'long' ? t('Long') : t('Short'), qty: num(position.qty, m.qtyDp), pnl: `${pnl >= 0 ? '+' : '−'}$${num(Math.abs(pnl), 2)}` }) });
      if (position.slPrice) out.push({ key: 'sl', drag: 'sl', price: position.slPrice, color: P.down, style: 2, width: 1, title: t('SL ↕') });
      if (position.tpPrice) out.push({ key: 'tp', drag: 'tp', price: position.tpPrice, color: P.up, style: 2, width: 1, title: t('TP ↕') });
    }
    if (breachPrice != null) out.push({ key: 'breach', price: breachPrice, color: P.warn, style: 1, width: 1, title: breachLabel || t('Loss limit') });
    for (const o of orders) {
      if (o.symbol !== symbol || o.status !== 'pending') continue;
      out.push({ key: 'o' + o.id, price: o.limitPrice, color: o.side === 'buy' ? P.orderBuy : P.orderSell, style: 3, width: 1,
        title: o.reduceOnly ? (o.type === 'stop' ? t('Stop close {qty}', { qty: num(o.qty, m.qtyDp) }) : t('Close {qty}', { qty: num(o.qty, m.qtyDp) }))
          : o.side === 'buy' ? t('Buy {qty}', { qty: num(o.qty, m.qtyDp) }) : t('Sell {qty}', { qty: num(o.qty, m.qtyDp) }) });
    }
    drawings.filter((d) => d.type === 'h').forEach((d, i) => out.push({ key: 'h' + i, price: d.price, color: P.hline, style: 0, width: 1, title: '' }));
    return out;
  }, [position, breachPrice, breachLabel, orders, symbol, drawings, m.qtyDp, P]);

  R.current.redrawLines = () => {
    const r = R.current; if (!r.candle) return;
    for (const l of r.lines || []) { try { r.candle.removePriceLine(l.line); } catch { /* ignore */ } }
    r.lines = lineSpec.map((s) => ({ ...s, line: r.candle.createPriceLine({ price: s.price, color: s.color, lineStyle: s.style, lineWidth: s.width, axisLabelVisible: true, title: s.title }) }));
  };
  useEffect(() => { if (!R.current.dragging) R.current.redrawLines(); }, [lineSpec]);

  // Trend lines: a 2-point line series each, stored per market and timeframe.
  R.current.redrawTrend = () => {
    const r = R.current; if (!r.chart) return;
    for (const s of r.trend || []) { try { r.chart.removeSeries(s); } catch { /* ignore */ } }
    r.trend = drawings.filter((d) => d.type === 't' && d.tf === tf).map((d) => {
      const s = r.chart.addSeries(L.LineSeries, { color: R.current.P.trend, lineWidth: 2, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
      const pts = [{ time: d.a.time, value: d.a.price }, { time: d.b.time, value: d.b.price }].sort((x, y) => x.time - y.time);
      if (pts[0].time === pts[1].time) pts[1].time += 1;
      s.setData(pts);
      return s;
    });
  };
  useEffect(() => { R.current.redrawTrend(); }, [drawings, tf]);

  // Clicks on the chart for drawing tools.
  R.current.onClick = (p) => {
    const r = R.current;
    if (tool === 'none' || !p || !p.point) return;
    const price = r.candle.coordinateToPrice(p.point.y);
    if (price == null) return;
    const pr = Number(price.toFixed(dp));
    if (tool === 'hline') { setDrawings((d) => [...d, { type: 'h', price: pr }]); setTool('none'); return; }
    if (tool === 'trend') {
      if (p.time == null) return;
      if (!r.pending) { r.pending = { time: p.time, price: pr }; setTool('trend2'); return; }
    }
    if (tool === 'trend2' && p.time != null && r.pending) {
      const a = r.pending; r.pending = null;
      setDrawings((d) => [...d, { type: 't', tf, a, b: { time: p.time, price: pr } }]);
      setTool('none');
    }
  };

  // Drag stop-loss / take-profit lines with the mouse or finger.
  useEffect(() => {
    const el = boxRef.current;
    const near = (y) => {
      const r = R.current; if (!r.candle) return null;
      let best = null;
      for (const l of r.lines || []) {
        if (!l.drag) continue;
        const ly = r.candle.priceToCoordinate(l.line.options().price);
        if (ly != null && Math.abs(ly - y) <= 7 && (!best || Math.abs(ly - y) < best.d)) best = { l, d: Math.abs(ly - y) };
      }
      return best && best.l;
    };
    const plotWidth = () => { const r = R.current; return r.chart ? el.clientWidth - r.chart.priceScale('right').width() : el.clientWidth; };
    const down = (e) => {
      if (!onMoveProtection) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left, y = e.clientY - rect.top;
      if (x > plotWidth()) return;
      const l = near(y); if (!l) return;
      e.preventDefault(); e.stopPropagation();
      const r = R.current;
      r.dragging = { l, start: l.line.options().price };
      r.chart.applyOptions({ handleScroll: false, handleScale: false });
      el.setPointerCapture && el.setPointerCapture(e.pointerId);
    };
    const move = (e) => {
      const r = R.current; if (!r.candle) return;
      const rect = el.getBoundingClientRect();
      const y = e.clientY - rect.top;
      if (r.dragging) {
        const p = r.candle.coordinateToPrice(y);
        if (p != null) r.dragging.l.line.applyOptions({ price: Number(p.toFixed(dp)), title: r.dragging.l.drag === 'sl' ? t('SL {price}', { price: num(p, dp) }) : t('TP {price}', { price: num(p, dp) }) });
        return;
      }
      const x = e.clientX - rect.left;
      el.style.cursor = x <= plotWidth() && near(y) ? 'ns-resize' : (tool !== 'none' ? 'crosshair' : '');
    };
    const up = async () => {
      const r = R.current; const d = r.dragging; if (!d) return;
      r.dragging = null;
      r.chart.applyOptions({ handleScroll: true, handleScale: true });
      const price = d.l.line.options().price;
      if (Math.abs(price - d.start) < 1 / 10 ** dp) { r.redrawLines(); return; }
      try { await onMoveProtection(d.l.drag, price); } catch { /* toast shown by caller */ }
      r.redrawLines();
    };
    el.addEventListener('pointerdown', down, true);
    el.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { el.removeEventListener('pointerdown', down, true); el.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, [onMoveProtection, dp, tool]);

  // ---------------------------------------------------------------- UI
  const toggleInd = (id) => setInd((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const fullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else if (sectionRef.current.requestFullscreen) sectionRef.current.requestFullscreen();
  };
  const lg = legend || (R.current.candles && R.current.candles.length ? (() => { const c = R.current.candles[R.current.candles.length - 1]; return { o: c.o, h: c.h, l: c.l, c: c.c }; })() : null);
  const chg = lg ? ((lg.c - lg.o) / lg.o) * 100 : null;
  const quick = async (side) => {
    if (!Number(qcQty)) return;
    setQcBusy(true);
    try { await onQuickOrder(side, Number(qcQty)); } finally { setQcBusy(false); }
  };
  const nDraw = drawings.filter((d) => d.type === 'h' || d.tf === tf).length;

  return (
    <section className={'chart tv' + (full ? ' full' : '')} aria-label={t('Price chart')} ref={sectionRef}>
      <div className="chart-toolbar">
        <div className="tf-group" role="group" aria-label={t('Timeframe')}>
          {MAIN_TF.map((i) => <button key={i} className={'tf' + (i === tf ? ' on' : '')} onClick={() => setTf(i)}>{TF_LABEL[i]}</button>)}
          <div className="menu-wrap" onClick={(e) => e.stopPropagation()}>
            <button className={'tf' + (MORE_TF.includes(tf) ? ' on' : '')} onClick={(e) => openMenu('tf', e, 120)} aria-label={t('More timeframes')}>{MORE_TF.includes(tf) ? TF_LABEL[tf] : '⋯'}</button>
            {menu === 'tf' && <div className="menu tf-menu floating" style={menuPos || undefined}>{MORE_TF.map((i) => <button key={i} className={'menu-item' + (i === tf ? ' on' : '')} onClick={() => { setTf(i); setMenu(null); }}>{TF_LABEL[i]}</button>)}</div>}
          </div>
        </div>
        <span className="tb-sep" />
        <div className="menu-wrap" onClick={(e) => e.stopPropagation()}>
          <button className={'tf tb' + (menu === 'ind' ? ' on' : '')} onClick={(e) => openMenu('ind', e, 280)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M3 17l5-6 4 4 8-9" /><path d="M3 21h18" /></svg>
            <span className="tb-label">{t('Indicators')}</span>{ind.length ? <span className="count">{ind.length}</span> : null}
          </button>
          {menu === 'ind' && (
            <div className="menu ind-menu floating" style={menuPos || undefined}>
              {INDICATORS.map((x) => (
                <label key={x.id} className="menu-item check-item">
                  <input type="checkbox" checked={ind.includes(x.id)} onChange={() => toggleInd(x.id)} />
                  {x.color && <span className="swatch" style={{ background: x.color }} />}<span>{t(x.label)}</span>
                </label>
              ))}
              <div className="menu-sep" />
              <button className="menu-item" onClick={() => { setMenu(null); onClassic(); }}>{t('Switch to classic chart')}</button>
            </div>
          )}
        </div>
        <button className={'tf tb' + (tool === 'hline' ? ' on' : '')} onClick={() => setTool(tool === 'hline' ? 'none' : 'hline')} title={t('Horizontal line: click on the chart')} aria-pressed={tool === 'hline'}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M3 12h18" /><circle cx="12" cy="12" r="2" /></svg><span className="hide-s">{t('H-line')}</span>
        </button>
        <button className={'tf tb' + (tool === 'trend' || tool === 'trend2' ? ' on' : '')} onClick={() => { R.current.pending = null; setTool(tool === 'none' || tool === 'hline' ? 'trend' : 'none'); }} title={t('Trend line: click two points')} aria-pressed={tool !== 'none' && tool !== 'hline'}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M4 19L20 5" /><circle cx="4" cy="19" r="2" /><circle cx="20" cy="5" r="2" /></svg><span className="hide-s">{t('Trend')}</span>
        </button>
        {nDraw > 0 && <button className="tf tb" onClick={() => setDrawings((d) => d.filter((x) => x.type === 't' && x.tf !== tf))} title={t('Remove drawings')}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" /></svg>
        </button>}
        <div className="grow" />
        <button className="tf tb" onClick={fullscreen} title={full ? t('Exit full screen') : t('Full screen')} aria-label={full ? t('Exit full screen') : t('Full screen')}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">{full ? <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" /> : <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />}</svg>
        </button>
      </div>
      <div className="chart-canvas">
        <div className="tv-box" ref={boxRef} />
        <div className="tv-legend">
          <span className="tv-sym">{m.label || m.base}<span className="muted"> · {TF_LABEL[tf]}</span></span>
          {lg && <span className="ohlc">O<b>{num(lg.o, dp)}</b>H<b>{num(lg.h, dp)}</b>L<b>{num(lg.l, dp)}</b>C<b className={lg.c >= lg.o ? 'up' : 'down'}>{num(lg.c, dp)}</b><b className={chg >= 0 ? 'up' : 'down'}>{chg >= 0 ? '+' : ''}{num(chg, 2)}%</b></span>}
        </div>
        {oneClick && (
          <div className="one-click" role="group" aria-label={t('One-click trading')}>
            <button className="oc sell" disabled={qcBusy || !Number(qcQty) || lastPrice == null} onClick={() => quick('sell')}><span>{t('Sell')}</span><b className="mono">{num(lastPrice, dp)}</b></button>
            <input className="oc-qty mono" inputMode="decimal" value={qcQty} placeholder={t('Size')} onChange={(e) => setQcQty(e.target.value.replace(/[^0-9.]/g, ''))} aria-label={t('One-click size in {asset}', { asset: m.base })} />
            <button className="oc buy" disabled={qcBusy || !Number(qcQty) || lastPrice == null} onClick={() => quick('buy')}><span>{t('Buy')}</span><b className="mono">{num(lastPrice, dp)}</b></button>
          </div>
        )}
        {tool !== 'none' && <div className="tool-hint">{tool === 'hline' ? t('Click the chart to place a horizontal line') : tool === 'trend' ? t('Click the first point of the trend line') : t('Click the second point')} · <button onClick={() => { R.current.pending = null; setTool('none'); }}>{t('Cancel')}</button></div>}
        {status === 'loading' && <div className="chart-msg">{t('Loading chart…')}</div>}
        {status === 'error' && <div className="chart-msg">{t('Chart history unavailable right now.')}</div>}
      </div>
    </section>
  );
}
